"use client";

import React, { useEffect, useState } from "react";
import reelsData from "../data/instagram.json";
import { track } from "../lib/track";

type Reel = { title: string; reelId: string };

const pick = <T,>(arr: T[], n: number) => [...arr].sort(() => 0.5 - Math.random()).slice(0, n);

const InstagramGallery: React.FC = () => {
  const [reels, setReels] = useState<Reel[]>([]);

  useEffect(() => {
    setReels(pick(reelsData, 6));
  }, []);

  const shuffle = () => {
    setReels(pick(reelsData, 6));
    track("click", { label: "IG Shuffle", component: "InstagramGallery" }, {
      event: "click", label: "IG Shuffle", component: "InstagramGallery",
      meta: { timestamp: Date.now() },
    });
  };

  if (!reels.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 py-10">
      <h2 className="text-3xl text-white font-semibold text-center mb-8 text-balance">
        🌿 From Our Instagram
      </h2>

      <div className="mb-4 flex justify-center">
        <button
          type="button"
          onClick={shuffle}
          className="rounded-full border border-white/40 bg-white/10 text-white text-sm px-4 py-2 backdrop-blur hover:bg-white/20 transition"
          aria-label="Shuffle reels"
        >
          Shuffle
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {reels.map(({ title, reelId }) => {
          const url = `https://www.instagram.com/reel/${reelId}/`;
          return (
            <a
              key={reelId}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-xl overflow-hidden shadow hover:shadow-lg transition border border-black/5 bg-white no-underline"
              onClick={() => track("click", { label: `Open IG Reel: ${reelId}`, component: "InstagramGallery" }, { event: "click", label: `Open IG Reel: ${reelId}`, component: "InstagramGallery", meta: { reelId, timestamp: Date.now() } })}
            >
              {/* thumbnail with play overlay */}
              <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={`/api/instagram/thumbnail/${reelId}`}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                {/* play overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-black/40 backdrop-blur group-hover:bg-black/55 transition shadow-lg">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </div>
              </div>

              {/* caption + CTA */}
              <div className="p-4 flex flex-col gap-3">
                <p className="text-sm font-medium text-[var(--color-brand)] leading-snug line-clamp-3">
                  {title}
                </p>
                <span className="self-start text-xs font-semibold text-[var(--color-brand)] border border-[var(--color-brand)]/30 rounded-full px-3 py-1 group-hover:bg-[var(--color-brand)] group-hover:text-white transition">
                  Watch on Instagram ↗
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
};

export default InstagramGallery;
